/**
 * The city hierarchy: `city -> roads | block -> building -> floor -> room -> stairs`.
 *
 * MOST OF THIS IS A RE-HOMING, not a new capability. `storeyHeight`, `roomWidth`, `corridorWidth`
 * and `windowPitch` live on RegionConfig today because there was nowhere else to put them, and
 * `BuildingPlan.ts` holds twenty-odd more as module constants that nothing outside the file can
 * reach. Here each lands on the kind that reads it.
 *
 * WHERE THE TWO DISAGREE, THE CONFIG WINS — because it is what the generator actually reads:
 * `city.ts` takes `cfg.roomWidth` / `cfg.corridorWidth` / `cfg.storeyHeight` / `cfg.windowPitch`,
 * and BuildingPlan's ROOM_WIDTH (8) and CORRIDOR_WIDTH (12) are module fallbacks that nothing
 * currently passes through. The pairs are: roomWidth 8 == 8 (agree), corridorWidth 10 vs 12 (config
 * wins). C3 resolves each to ONE owner and deletes the loser.
 *
 * The constants that are NOT config today (slab, partition, window size and sill, the stair
 * geometry) are declared here at their module values; C3 is what wires them through, and until then
 * the catalog is ahead of the generator by exactly those knobs. That is deliberate and asserted —
 * catalog.test.ts names them, so the list cannot grow silently.
 */
export const CITY = {
    kind: 'city',
    label: 'City',
    blurb: 'City regions are laid out on their own street grid, anchored on the region centre and '
        + 'turned to its own angle.',
    capability: 'group',
    maxLod: -1,
    expandAtLod: 5,
    children: ['roads', 'block'],
    toggle: { key: 'enabled', def: true },
    params: [
        { t: 'number', key: 'frequency', label: 'Frequency', share: true, min: 0, max: 100, step: 5, def: 20, unit: '%',
            desc: 'How common this region type is, against the others. Unticking it is a frequency of zero.' },
        { t: 'material', key: 'wallMaterial', label: 'Wall material', def: 'concrete',
            desc: 'The colour this type’s stretch of boundary wall is drawn in.' },
    ],
};
export const ROADS = {
    kind: 'roads',
    label: 'Roads',
    blurb: 'The carriageway grid, drawn as paving. It switches off at the range where a road would '
        + 'smear into a car park, rather than degrading into one.',
    capability: 'field',
    maxLod: 5,
    expandAtLod: -1,
    children: [],
    toggle: { key: 'enabled', def: true },
    params: [
        { t: 'number', key: 'spacing', label: 'Block size', min: 15, max: 120, step: 5, def: 40, unit: 'm',
            desc: 'Distance between road centrelines — one city block. Lower = a denser street grid.' },
        // A road narrower than a distant chunk's voxel cannot be drawn at ALL, which is why the grid
        // switches off at that level rather than degrading into a car park. See CityLayout.
        { t: 'number', key: 'width', label: 'Road width', min: 1, max: 20, step: 1, def: 6, unit: 'm',
            desc: 'Carriageway width. Also how far out the grid stays visible — narrow roads fade sooner.' },
    ],
};
export const BLOCK = {
    kind: 'block',
    label: 'Blocks',
    blurb: 'One tiered tower per block, seated on the ground it stands on. Individual towers are drawn '
        + 'out to the fifth ring; past that a city is its block silhouette, at the same heights.',
    capability: 'both',
    maxLod: 5,
    expandAtLod: 5,
    children: ['building'],
    toggle: { key: 'enabled', def: true },
    params: [
        { t: 'number', key: 'inset', label: 'Setback', min: 0, max: 12, step: 0.5, def: 2, unit: 'm',
            desc: 'Pavement between the road edge and the building. Higher = smaller buildings.' },
        { t: 'number', key: 'minHeight', label: 'Min height', min: 1, max: 50, step: 1, def: 6, unit: 'm',
            desc: 'Shortest a block can be. Raises the whole skyline’s floor.' },
        { t: 'number', key: 'maxHeight', label: 'Max height', min: 10, max: 500, step: 5, def: 160, unit: 'm',
            desc: 'Tallest a block can be. Only a few get near it — see Height bias.' },
        { t: 'number', key: 'heightBias', label: 'Height bias', min: 1, max: 8, step: 0.5, def: 3,
            desc: 'How rare tall towers are. 1 = a wall of towers; 3 = mostly low, a few landmarks.' },
        { t: 'number', key: 'downtownFalloff', label: 'Downtown', min: 0, max: 100, step: 5, def: 75, unit: '%',
            desc: 'How much shorter the city gets toward its edge. 0 = uniform; 100 = tall in the middle.' },
    ],
};
export const BUILDING = {
    kind: 'building',
    label: 'Buildings',
    blurb: 'The shell: a tiered mass on a plinth, with a parapet. Its tiers derive from the block, so '
        + 'crossing the level where towers stop being drawn individually changes the footprint and never '
        + 'the height.',
    capability: 'volume',
    maxLod: 5,
    expandAtLod: 1,
    children: ['floor'],
    params: [
        { t: 'number', key: 'wallThickness', label: 'Wall thickness', min: 0.25, max: 2, step: 0.25, def: 0.75, unit: 'm',
            desc: 'Three fine voxels. Below three, a wall’s two faces dither along its length.' },
        { t: 'number', key: 'parapetHeight', label: 'Parapet height', min: 0, max: 4, step: 0.25, def: 1, unit: 'm',
            desc: 'The lip around a roof. 0 = a flat top.' },
    ],
};
export const FLOOR = {
    kind: 'floor',
    label: 'Floors',
    blurb: 'Hollow shells with windows and a door from the third ring in, floor slabs from the second.',
    capability: 'volume',
    maxLod: 1,
    expandAtLod: 0,
    children: ['room'],
    toggle: { key: 'enabled', def: true },
    params: [
        { t: 'number', key: 'storeyHeight', label: 'Storey height', min: 2.5, max: 6, step: 0.25, def: 3.5, unit: 'm',
            desc: 'Metres per floor. Also decides how many floors a tower has.' },
        { t: 'number', key: 'windowPitch', label: 'Window spacing', min: 2, max: 12, step: 0.5, def: 4, unit: 'm',
            desc: 'Metres between windows along a wall. Lower = more windows.' },
        { t: 'number', key: 'windowSize', label: 'Window size', min: 0.5, max: 4, step: 0.25, def: 1.5, unit: 'm',
            desc: 'An opening smaller than a voxel is a dimple, not a window.' },
        { t: 'number', key: 'windowSill', label: 'Sill height', min: 0, max: 2, step: 0.25, def: 1, unit: 'm',
            desc: 'How far a window sits above its own floor.' },
        { t: 'number', key: 'slabThickness', label: 'Slab thickness', min: 0.25, max: 1.5, step: 0.25, def: 0.5, unit: 'm',
            desc: 'A floor slab. Thinner than half a metre and it winks out along its own length.' },
    ],
};
export const ROOM = {
    kind: 'room',
    label: 'Rooms',
    blurb: 'A corridor spine through the stair core, with rooms slicing the strips either side and a '
        + 'doorway onto the corridor from each.',
    capability: 'volume',
    maxLod: 0,
    expandAtLod: 0,
    children: ['stairs'],
    params: [
        { t: 'number', key: 'roomWidth', label: 'Room width', min: 2, max: 12, step: 0.5, def: 8, unit: 'm',
            desc: 'Target width of a room along the corridor. Lower = more, smaller rooms.' },
        { t: 'number', key: 'corridorWidth', label: 'Corridor width', min: 1, max: 12, step: 0.25, def: 10, unit: 'm',
            desc: 'Corridor width from the stairs to the rooms — a floor, widened to clear the core.' },
        { t: 'number', key: 'partitionThickness', label: 'Partition thickness', min: 0.25, max: 1, step: 0.25, def: 0.25, unit: 'm',
            desc: 'An interior wall. One fine voxel, the lattice’s own floor.' },
    ],
};
export const STAIRS = {
    kind: 'stairs',
    label: 'Stairs',
    blurb: 'A switchback in a core clamped inside the narrowest tier, with a landing at both ends of '
        + 'every run. Every storey can be walked to from the front door, and a test walks it.',
    capability: 'volume',
    maxLod: 0,
    expandAtLod: -1,
    children: [],
    params: [
        { t: 'number', key: 'riser', label: 'Riser', min: 0.25, max: 0.75, step: 0.25, def: 0.25, unit: 'm',
            desc: 'Height of one step. One fine voxel is the floor — below it a flight comes out lacy.' },
        { t: 'number', key: 'tread', label: 'Going', min: 0.25, max: 1.5, step: 0.25, def: 0.5, unit: 'm',
            desc: 'Depth of one step. Twice the riser is about 27° — a stair rather than a ladder.' },
        { t: 'number', key: 'coreWidth', label: 'Shaft width', min: 4, max: 12, step: 1, def: 6, unit: 'm',
            desc: 'Two flights side by side. A single-width core seals its own stairwell at the top.' },
        { t: 'number', key: 'coreRun', label: 'Shaft length', min: 6, max: 26, step: 1, def: 13, unit: 'm',
            desc: 'Two landings plus fourteen risers. Longer flights are shallower.' },
        { t: 'number', key: 'clearance', label: 'Clearance', min: 0, max: 4, step: 0.5, def: 2, unit: 'm',
            desc: 'Open floor kept clear round the shaft, so no wall lands on a flight.' },
    ],
};
export const CITY_KINDS = [CITY, ROADS, BLOCK, BUILDING, FLOOR, ROOM, STAIRS];
//# sourceMappingURL=city.js.map